import { useLayoutEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import SignInPage from './pages/SignInPage';
import DashboardPage from './pages/DashboardPage';
import ApplicationPage from './pages/ApplicationPage';
import DocumentsPage from './pages/DocumentsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import CheckInsPage from './pages/CheckInsPage';
import OnboardingPage from './pages/OnboardingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import RefereeResponsePage from './pages/RefereeResponsePage';
import NotificationsPage from './pages/NotificationsPage';
import ArrivalInformationPage from './pages/ArrivalInformationPage';
import LocalTravelPage from './pages/LocalTravelPage';
import AppLayout from './components/AppLayout';
import PublicShell from './components/PublicShell';
import TermsGateModal from './components/TermsGateModal';
import LegalPage from './pages/LegalPage';
import { auth } from './lib/api';
import { PortalContextProvider } from './hooks/usePortalContext';
import { captureBrowserPageView, updateBrowserSessionRecording } from './lib/posthog';

function PostHogPageViewTracker() {
  const location = useLocation();
  // Layout phase is intentional: stop replay before MutationObserver callbacks
  // can serialize a newly committed tokenized public route.
  useLayoutEffect(() => {
    updateBrowserSessionRecording(location.pathname);
    captureBrowserPageView(window.location.href);
  }, [location.pathname, location.search]);
  return null;
}

function RequireAuth({ children }: { children: JSX.Element }) {
  if (!auth.isAuthenticated()) {
    // A present-but-expired token would otherwise linger in sessionStorage
    // until the next 401 round-trip cleared it; drop it here so a stale
    // session can't outlive its own exp claim in storage.
    if (auth.getToken()) auth.clear();
    return <Navigate to="/sign-in" replace />;
  }
  return children;
}

/**
 * The legal / contact pages are reachable logged-out (linked from the sign-in
 * screen) but keep the full app shell when a session exists, so the footer
 * links don't dump a signed-in user outside the app.
 */
function LegalShell() {
  return auth.isAuthenticated() ? (
    <PortalContextProvider>
      <AppLayout />
    </PortalContextProvider>
  ) : (
    <PublicShell />
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <PostHogPageViewTracker />
      {/* form-engine-portable raises upload/save failures via react-hot-toast —
          without a mounted Toaster those errors are silently dropped. */}
      <Toaster position="top-center" />
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        {/* Public referee form — the tokenized link Globus emails referees
            (…/reference/respond?token=…). Third parties, no portal account. */}
        <Route path="/reference/respond" element={<RefereeResponsePage />} />
        <Route element={<RequireAuth><PortalContextProvider><TermsGateModal><AppLayout /></TermsGateModal></PortalContextProvider></RequireAuth>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/application" element={<ApplicationPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/check-ins" element={<CheckInsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/travel" element={<LocalTravelPage />} />
          {/* AP-13: notifications inbox */}
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* Task #1673: post-placement arrival information */}
          <Route path="/arrival-information" element={<ArrivalInformationPage />} />
        </Route>
        <Route element={<LegalShell />}>
          <Route path="/privacy-policy" element={<LegalPage kind="privacy_policy" />} />
          <Route path="/disclaimer" element={<LegalPage kind="disclaimer" />} />
          <Route path="/contact" element={<LegalPage kind="contact" />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
